import * as XLSX from 'xlsx'

/**
 * Excel 列定义
 */
export interface KLExcelColumn {
	/** 列标题 */
	title: string
	/** 数据字段名 */
	dataIndex: string
	/** 列宽（字符数） */
	width?: number
	/** 自定义格式化函数 */
	render?: (value: any, record: any, index: number) => string | number
}

/**
 * Excel 导出配置
 */
export interface KLExcelExportOptions {
	/** 文件名（不含扩展名） */
	filename: string
	/** 工作表名称 */
	sheetName?: string
	/** 是否自动调整列宽 */
	autoWidth?: boolean
}

/**
 * Excel 工具类
 */
export const klExcel = {
	/**
	 * 导出数据到 Excel 文件
	 * @param data 数据数组
	 * @param columns 列定义
	 * @param options 导出配置
	 */
	exportToExcel<T extends Record<string, any>>(
		data: T[],
		columns: KLExcelColumn[],
		options: KLExcelExportOptions
	): void {
		const { filename, sheetName = 'Sheet1', autoWidth = true } = options

		// 构建表头
		const headers = columns.map((col) => col.title)

		// 构建数据行
		const rows = data.map((record, index) => {
			return columns.map((col) => {
				const value = record[col.dataIndex]
				if (col.render) {
					return col.render(value, record, index)
				}
				return value ?? ''
			})
		})

		// 合并表头和数据
		const sheetData = [headers, ...rows]

		// 创建工作表
		const worksheet = XLSX.utils.aoa_to_sheet(sheetData)

		// 设置列宽
		if (autoWidth) {
			const colWidths = columns.map((col, colIndex) => {
				if (col.width) {
					return { wch: col.width }
				}
				// 自动计算列宽
				let maxWidth = col.title.length
				rows.forEach((row) => {
					const cellValue = String(row[colIndex] ?? '')
					const cellWidth = this._getStringWidth(cellValue)
					if (cellWidth > maxWidth) {
						maxWidth = cellWidth
					}
				})
				return { wch: Math.min(maxWidth + 2, 50) }
			})
			worksheet['!cols'] = colWidths
		}

		// 创建工作簿
		const workbook = XLSX.utils.book_new()
		XLSX.utils.book_append_sheet(workbook, worksheet, sheetName)

		// 导出文件
		XLSX.writeFile(workbook, `${filename}.xlsx`)
	},

	/**
	 * 从 JSON 数据直接导出（简化版）
	 * @param data 数据数组
	 * @param filename 文件名
	 * @param sheetName 工作表名称
	 */
	exportJsonToExcel<T extends Record<string, any>>(
		data: T[],
		filename: string,
		sheetName: string = 'Sheet1'
	): void {
		const worksheet = XLSX.utils.json_to_sheet(data)
		const workbook = XLSX.utils.book_new()
		XLSX.utils.book_append_sheet(workbook, worksheet, sheetName)
		XLSX.writeFile(workbook, `${filename}.xlsx`)
	},

	/**
	 * 导出多个工作表
	 * @param sheets 工作表配置数组
	 * @param filename 文件名
	 */
	exportMultiSheet(
		sheets: Array<{
			name: string
			data: Record<string, any>[]
			columns?: KLExcelColumn[]
		}>,
		filename: string
	): void {
		const workbook = XLSX.utils.book_new()

		sheets.forEach((sheet) => {
			let worksheet: XLSX.WorkSheet

			if (sheet.columns) {
				const headers = sheet.columns.map((col) => col.title)
				const rows = sheet.data.map((record, index) => {
					return sheet.columns!.map((col) => {
						const value = record[col.dataIndex]
						if (col.render) {
							return col.render(value, record, index)
						}
						return value ?? ''
					})
				})
				worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows])
			} else {
				worksheet = XLSX.utils.json_to_sheet(sheet.data)
			}

			XLSX.utils.book_append_sheet(workbook, worksheet, sheet.name)
		})

		XLSX.writeFile(workbook, `${filename}.xlsx`)
	},

	/**
	 * 从 Blob 下载 Excel 文件
	 * @param blob Blob 数据
	 * @param filename 文件名
	 */
	downloadBlob(blob: Blob, filename: string): void {
		const url = window.URL.createObjectURL(blob)
		const link = document.createElement('a')
		link.href = url
		link.download = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`
		document.body.appendChild(link)
		link.click()
		document.body.removeChild(link)
		window.URL.revokeObjectURL(url)
	},

	/**
	 * 读取 Excel 文件
	 * @param file File 对象
	 * @returns 解析后的数据
	 */
	async readExcel<T = Record<string, any>>(file: File): Promise<T[]> {
		return new Promise((resolve, reject) => {
			const reader = new FileReader()
			reader.onload = (e) => {
				try {
					const data = new Uint8Array(e.target?.result as ArrayBuffer)
					const workbook = XLSX.read(data, { type: 'array' })
					const firstSheetName = workbook.SheetNames[0]
					const worksheet = workbook.Sheets[firstSheetName]
					const jsonData = XLSX.utils.sheet_to_json<T>(worksheet)
					resolve(jsonData)
				} catch (error) {
					reject(error)
				}
			}
			reader.onerror = reject
			reader.readAsArrayBuffer(file)
		})
	},

	/**
	 * 计算字符串宽度（中文算2，英文算1）
	 */
	_getStringWidth(str: string): number {
		let width = 0
		for (let i = 0; i < str.length; i++) {
			const charCode = str.charCodeAt(i)
			if (charCode > 255) {
				width += 2
			} else {
				width += 1
			}
		}
		return width
	},
}

export type { XLSX }

